// SPDX-License-Identifier: Elastic-2.0
/**
 * useTheme — Zustand store for the user's theme preference.
 *
 * Three modes:
 *   - "dark"  (default): always dark
 *   - "light": always light
 *   - "auto":  follow the OS via prefers-color-scheme
 *
 * Persisted to `localStorage["mirlai_theme"]`. The inline blocking
 * script in `index.html` reads the same key before React mounts so
 * the first paint already has the right `<html data-theme>` — see
 * `theme/INTEGRATION.md`.
 *
 * This module owns ONLY the user preference. Resolving "auto" into an
 * effective theme and applying it to the DOM live in `ThemeProvider`.
 */
import { create } from "zustand";

export type ThemeMode = "dark" | "light" | "auto";

/** localStorage key shared with the index.html inline script. */
export const THEME_STORAGE_KEY = "mirlai_theme";

/** Theme mode used when no preference is found or storage is broken. */
export const DEFAULT_THEME: ThemeMode = "dark";

export interface ThemeState {
  /** The user's chosen mode — what they picked, not what's currently rendered. */
  theme: ThemeMode;
  /** Update + persist the user's mode. */
  setTheme: (next: ThemeMode) => void;
}

/**
 * Read the persisted preference. Tolerates SSR / privacy modes /
 * legacy values by falling back to the default.
 */
function readStoredTheme(): ThemeMode {
  if (typeof window === "undefined") {
    return DEFAULT_THEME;
  }
  try {
    const raw = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (raw === "dark" || raw === "light" || raw === "auto") {
      return raw;
    }
  } catch {
    // localStorage may throw in privacy mode / disabled storage.
  }
  return DEFAULT_THEME;
}

function writeStoredTheme(theme: ThemeMode): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // Storage may be disabled — DOM stays correct for the session.
  }
}

export const useTheme = create<ThemeState>((set) => ({
  theme: readStoredTheme(),
  setTheme: (next) => {
    writeStoredTheme(next);
    set({ theme: next });
  },
}));
